import dayjs, { dateInputFieldFormat } from '~libs/dayjs';
import { downloadCsv } from '~libs/csv';

export interface BulkCopyCsvRow {
  client_name: string;
  // budget_id is retained for the deep link (budget_url); the displayed identifier column is budget_reference.
  budget_id: string;
  budget_reference: string;
  start_date: string;
  end_date: string;
  services_removed: number;
  /**
   * Services carried into a different contribution category, named. Its own column rather than part
   * of `services_removed`: a provider reconciling the export has to be able to tell a service that
   * is gone from one that has moved, which a combined count never allowed.
   */
  services_moved: string;
  services_price_missing: number;
  needs_review: boolean;
}

// Column identifiers in AC order. NB: column 2 is `budget_reference` (the human-facing
// ref) by explicit request, superseding the AC's `budget_id`; the id still backs `budget_url`.
const HEADERS = [
  'client_name',
  'budget_reference',
  'budget_url',
  'start_date',
  'end_date',
  'services_removed',
  'services_moved',
  'services_price_missing',
  'status',
];

const budgetUrl = (id: string): string => `${window.location.origin}/support-at-home/budgets/${id}`;

// fileBaseName is stamped with today's date to form `{fileBaseName}-YYYY-MM-DD.csv`.
// Step 4 (results) uses the default; step 3 (preview) overrides it.
export const exportBulkCopyReviewCsv = (rows: BulkCopyCsvRow[], fileBaseName = 'bulk-copy-review'): void => {
  const body = rows.map((row) => [
    row.client_name,
    row.budget_reference,
    budgetUrl(row.budget_id),
    row.start_date,
    row.end_date,
    String(row.services_removed),
    row.services_moved,
    String(row.services_price_missing),
    row.needs_review ? 'needs_review' : 'ok',
  ]);
  downloadCsv(`${fileBaseName}-${dayjs().format(dateInputFieldFormat)}.csv`, HEADERS, body);
};

/**
 * Budgets that could NOT be copied (TAV-2649a). Kept as its own row shape and file rather than folded into
 * the review CSV: these budgets have no copy to report counts for, and the actionable column is the reason.
 * The url points at the SOURCE budget — that's the record whose data needs fixing before a retry.
 */
export interface BulkCopyFailureCsvRow {
  client_name: string | null;
  budget_id: string;
  budget_reference: string | null;
  reason: string;
  /** Mirrors the table's "Can Retry" column, so the exported list is as actionable as the on-screen one. */
  retryable: boolean;
}

const FAILURE_HEADERS = ['client_name', 'budget_reference', 'budget_url', 'reason', 'can_retry'];

export const exportBulkCopyFailuresCsv = (
  rows: BulkCopyFailureCsvRow[],
  fileBaseName = 'bulk-copy-not-copied',
): void => {
  const body = rows.map((row) => [
    row.client_name ?? '',
    row.budget_reference ?? '',
    budgetUrl(row.budget_id),
    row.reason,
    row.retryable ? 'yes' : 'no',
  ]);
  downloadCsv(`${fileBaseName}-${dayjs().format(dateInputFieldFormat)}.csv`, FAILURE_HEADERS, body);
};
